import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getPlatform } from '@shared/platform'
import { useNpsRefuse, useNpsReview } from '@entities/nps'
import { getMascotFace } from '../lib/mascotFaces'
import {
  COMMENT_MAX_LENGTH,
  SCORE_MAX,
  SCORE_MIN,
  type SurveyDefinition,
} from '../lib/surveyConfig'
import { NpsScale } from './NpsScale'
import styles from './NpsModal.module.css'

/** Столько длится уход окна — синхронизировано с анимацией в CSS. */
const CLOSE_MS = 240

interface NpsModalProps {
  survey: SurveyDefinition
  /** Название фичи с бэка — подставляем, если у нас нет своего заголовка. */
  featureTitle: string
  onClose: () => void
}

/**
 * Окно опроса: оценка, необязательный комментарий и благодарность после
 * отправки. Закрытие отправляет отказ — дальше срок следующего показа считает
 * бэк.
 */
export const NpsModal = ({ survey, featureTitle, onClose }: NpsModalProps) => {
  const [score, setScore] = useState<number | null>(null)
  const [text, setText] = useState('')
  const [bonus, setBonus] = useState<number | null>(null)
  const [isClosing, setClosing] = useState(false)

  const review = useNpsReview()
  const refuse = useNpsRefuse()

  // В Telegram протяжка вниз сворачивает мини-апп. Пока открыт опрос с полем
  // ввода, это закрыло бы приложение вместе с недописанным отзывом.
  useEffect(() => {
    const platform = getPlatform()
    platform.disableVerticalSwipes()
    return () => platform.enableVerticalSwipes()
  }, [])

  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(closeTimerRef.current), [])

  /**
   * Запускает уход окна и снимает его после анимации.
   *
   * Запрос отказа при этом уже летит: ждать ответа незачем — метод
   * идемпотентен, а держать окно на экране ради него значит показывать
   * зависшую модалку.
   */
  const startClose = () => {
    if (isClosing) return
    setClosing(true)
    closeTimerRef.current = setTimeout(onClose, CLOSE_MS)
  }

  const isThanks = bonus !== null

  const handleSubmit = () => {
    if (score === null || review.isLoading) return
    review.mutate(
      {
        slug: survey.slug,
        score,
        text: text.trim().slice(0, COMMENT_MAX_LENGTH),
        page: survey.page,
      },
      {
        onSuccess: data => setBonus(data.bonus_awarded),
        // 409 значит «отзыв уже записан»: для человека это тот же успех.
        onError: error =>
          error.response?.status === 409 ? setBonus(0) : undefined,
      }
    )
  }

  // Закрытие окна — это отказ: отдельной кнопки в макете нет, а бэку нужен
  // сигнал, чтобы отложить следующий показ. После отправленного отзыва
  // отказывать уже не от чего.
  const handleDismiss = () => {
    if (isClosing) return
    if (!isThanks) refuse.mutate({ slug: survey.slug })
    startClose()
  }

  const isFailed = review.isError && review.error?.response?.status !== 409

  return createPortal(
    <div
      className={`${styles.overlay} ${isClosing ? styles.closing : ''}`}
      role="presentation"
      // Клик по подложке закрывает окно: своего крестика в макете нет, а без
      // выхода человек оказался бы заперт, пока не поставит оценку.
      onClick={handleDismiss}
    >
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
      >
        <img
          className={styles.mascot}
          src={getMascotFace(isThanks ? SCORE_MAX : score, SCORE_MIN, SCORE_MAX)}
          alt=""
        />

        {/* key переключает состояние: React пересоздаёт блок, и появление
            благодарности проигрывается анимацией, а не подменяется рывком. */}
        <div className={styles.content} key={isThanks ? 'thanks' : 'form'}>
          {isThanks ? (
            <>
              <div className={styles.heading}>
                <h2 className={styles.title}>Спасибо</h2>
              </div>
              {/* Про баллы молчим: бонусная программа ещё не запущена,
                  показывать начисление некуда — баланса в приложении нет.
                  Ответ бэка с `bonus_awarded` при этом никуда не делся. */}
              <p className={styles.thanksText}>
                Ваш отзыв поможет нам стать лучше.
              </p>
              <button
                type="button"
                className={styles.submit}
                onClick={startClose}
              >
                Закрыть
              </button>
            </>
          ) : (
            <>
              <div className={styles.heading}>
                <h2 className={styles.title}>{survey.title || featureTitle}</h2>
                <p className={styles.subtitle}>{survey.subtitle}</p>
              </div>

              <NpsScale
                min={SCORE_MIN}
                max={SCORE_MAX}
                value={score}
                onChange={setScore}
              />

              <textarea
                className={styles.comment}
                value={text}
                onChange={e => setText(e.target.value)}
                maxLength={COMMENT_MAX_LENGTH}
                placeholder="Расскажите, что вам понравилось или что можно улучшить"
                aria-label="Комментарий"
              />

              {isFailed && (
                <p className={styles.error}>
                  Не удалось отправить отзыв. Попробуйте ещё раз.
                </p>
              )}

              <button
                type="button"
                className={styles.submit}
                disabled={score === null || review.isLoading}
                onClick={handleSubmit}
              >
                {review.isLoading ? 'Отправляем…' : 'Поделиться'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
